import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, FormControl, IconButton, InputAdornment,
  InputLabel, LinearProgress, MenuItem, Paper, Select, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import StorefrontIcon from '@mui/icons-material/Storefront';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Client from './components/Client';
import Visibility from './Visibility';

const marketplaceProviders = {
  GLOVO_POS: {
    label: 'Glovo',
    request: (client, action, data) => client.marketplaceRequest(action, data),
  },
};

const catalogIssueGuidance = {
  INVALID_LOCAL_CATALOG_PRICE: {
    title: 'Invalid catalog price',
    description: 'This catalog item has a price that cannot be published to Glovo.',
    action: 'Correct the price in the effective company pricelist, then generate the preview again.',
  },
  COMBO_COMPONENT_MAPPING_NOT_EXACT: {
    title: 'Combo component is not available',
    description: 'A combo component cannot be matched to an eligible product in the effective company pricelist.',
    action: 'Correct the component product and its pricelist row, then generate the preview again.',
  },
  CATALOG_PRODUCT_UNSUPPORTED: {
    title: 'Product is not eligible for the catalog',
    description: 'A product selected for the catalog does not meet one or more publication requirements.',
    action: 'Correct the product settings listed below, then generate the preview again.',
  },
};

const failedCheckGuidance = {
  PRICE_INVALID: {
    title: 'Price is invalid',
    description: 'The effective price is negative, is not a finite number, or has more than two decimal places.',
    action: 'Set a non-negative price with no more than two decimal places in the effective company pricelist.',
  },
  NOT_FOUND_IN_EFFECTIVE_COMPANY_LIST: {
    title: 'Missing from the effective pricelist',
    description: 'No exact price row was found for the target product in the effective company pricelist.',
    action: 'Add the target product to the effective company pricelist and make its price row active.',
  },
  NOT_FOR_SALE: {
    title: 'Not marked for sale',
    description: 'The product’s “for sale” setting is disabled.',
    action: 'Enable the product for sale if it should be available on Glovo.',
  },
  STATUS_NOT_ACTIVE: {
    title: 'Product is inactive',
    description: 'The product is not in the active status required for publication.',
    action: 'Activate the product if it should be available on Glovo.',
  },
  NOT_SELLABLE: {
    title: 'Product is not sellable',
    description: 'The product’s sellable setting is disabled.',
    action: 'Enable the sellable setting if the product should be available on Glovo.',
  },
  NOT_DELIVERABLE: {
    title: 'Product is not deliverable',
    description: 'The product’s deliverable setting is disabled.',
    action: 'Enable delivery for the product if it should be available on Glovo.',
  },
  NOT_SOLD_ALONE: {
    title: 'Product is not sold individually',
    description: 'The product is not configured to be sold as a standalone catalog item.',
    action: 'Enable the sold-alone setting or remove the product from the standalone catalog.',
  },
  PRICE_ROW_INACTIVE: {
    title: 'Pricelist row is inactive',
    description: 'The matching row in the effective company pricelist is inactive.',
    action: 'Activate the product’s row in the effective company pricelist.',
  },
  CATEGORY_MISSING: {
    title: 'Category is missing',
    description: 'The product has no category.',
    action: 'Assign the product to a category that can be published.',
  },
  CATEGORY_NOT_FOR_SALE: {
    title: 'Category is not for sale',
    description: 'The product’s category is not enabled for sale.',
    action: 'Enable the category for sale or move the product to an eligible category.',
  },
  SUBCATEGORY_MISSING: {
    title: 'Subcategory is missing',
    description: 'The product has no subcategory.',
    action: 'Assign the product to a subcategory that can be published.',
  },
  SUBCATEGORY_NOT_FOR_SALE: {
    title: 'Subcategory is not for sale',
    description: 'The product’s subcategory is not enabled for sale.',
    action: 'Enable the subcategory for sale or move the product to an eligible subcategory.',
  },
};

const entityTypeLabels = {
  Product: 'Product', ProductCombo: 'Combo', ProductComboProduct: 'Combo component',
  ProductComboCategory: 'Combo category', MenuProduct: 'Catalog item',
  Attribute: 'Option', AttributeGroup: 'Option group',
};
const humanizeCode = value => String(value || '').toLowerCase().split('_')
  .filter(Boolean).map(word => word[0].toUpperCase() + word.slice(1)).join(' ');
const diagnosticEntity = (source, fallback = {}) => {
  if (!source && !fallback.entityType) return null;
  const entity = source || fallback;
  return {
    type: entityTypeLabels[entity.entityType] || humanizeCode(entity.entityType) || 'Catalog item',
    id: entity.entityId || '', name: entity.entityName || '',
  };
};
const marketplacePayload = error => error?.responseJSON || error || {};

function marketplaceIssue(error) {
  const payload = marketplacePayload(error);
  const diagnostic = payload.diagnostic;
  if (!payload.errorCode || !diagnostic || typeof diagnostic !== 'object') return null;
  const guidance = catalogIssueGuidance[payload.errorCode] || {
    title: humanizeCode(payload.errorCode),
    description: 'The catalog could not be prepared because one of its items failed a local check.',
    action: 'Correct the affected catalog data, then generate the preview again.',
  };
  const checks = (Array.isArray(diagnostic.failedChecks) ? diagnostic.failedChecks : [])
    .filter(code => typeof code === 'string').map(code => ({
      code,
      ...(failedCheckGuidance[code] || {
        title: humanizeCode(code),
        description: 'This catalog requirement was not satisfied.',
        action: 'Review and correct this setting on the affected item.',
      }),
    }));
  return {
    code: payload.errorCode, stage: diagnostic.stage || '', ...guidance, checks,
    entity: diagnosticEntity(null, diagnostic),
    parent: diagnosticEntity(diagnostic.parent),
    target: diagnosticEntity(diagnostic.target),
    actions: [...new Set((checks.length ? checks.map(check => check.action) : [guidance.action]).filter(Boolean))],
  };
}

function marketplaceError(error) {
  const payload = marketplacePayload(error);
  const code = payload.errorCode;
  if (code === 'MENU_CHANGED') return 'The catalog changed. Run Preview again, review it and validate before uploading.';
  if (code === 'MENU_DAILY_LIMIT') return 'Local daily upload limit reached. Try again on the next Bucharest day.';
  if (code === 'UPLOAD_PENDING_RECONCILIATION') return 'An upload is pending or unknown. Refresh locations and check its status before trying again.';
  if (payload.synchronizationState === 'UNKNOWN') return 'Upload outcome unknown. Do not retry; reconcile the attempt with Glovo.';
  if (code && payload.diagnostic) return (catalogIssueGuidance[code]?.title || humanizeCode(code));
  if (code) return code;
  if (error?.status === 403) return 'You do not have permission to manage marketplace menus.';
  if (error?.status === 401) return 'Your session expired. Sign in again.';
  return 'The request could not be completed. Refresh locations to recover any upload transaction before retrying.';
}

function temporaryClosureError(error) {
  const payload = marketplacePayload(error);
  if (payload.operationState === 'UNKNOWN' || error?.status === 0) {
    return 'Closure outcome unknown. Do not repeat the change; refresh the closure status first.';
  }
  if (payload.errorCode === 'TEMPORARY_CLOSING_UNTIL_INVALID') return 'Choose a future closing time.';
  if (payload.errorCode === 'CLOSURE_AUDIT_UNAVAILABLE') return 'The change was not sent because its audit record could not be created.';
  if (payload.errorCode === 'GLOVO_POS_MENU_DISABLED') return 'Glovo mutations are disabled by backend configuration.';
  if (error?.status === 403) return 'You do not have permission to manage this store.';
  if (error?.status === 401) return 'Your session expired. Sign in again.';
  return payload.errorCode || 'Glovo closure status could not be confirmed.';
}

export function futureClosureTimestamp(durationMinutes, now = Date.now()) {
  return new Date(now + durationMinutes * 60 * 1000)
    .toISOString().replace('Z', '+00:00');
}

export function bucharestTime(value) {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return new Intl.DateTimeFormat('ro-RO', {
    timeZone: 'Europe/Bucharest',
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(parsed);
}

function pricelistLabel(preview) {
  if (!preview) return 'Not checked';
  const resolved = preview.resolved;
  const list = resolved.companyListDetails;
  return `${resolved.channelCompany.name} / effective ${list.dateFrom} / v${list.version} (${list.iref})`;
}

function uploadLabel(upload) {
  if (!upload) return '';
  if (upload.status === 'SUCCESS') return 'Upload success';
  if (pendingStatusExpired(upload)) return 'Upload outcome unknown — status expired after 24 hours';
  if (['PROCESSING', 'SENDING'].includes(upload.status)) return 'Upload processing';
  if (upload.status === 'UNKNOWN') return 'Upload unknown — reconciliation required';
  return `Upload failed: ${upload.status || upload.errorCode || 'request failed'}`;
}

const terminalFailure = upload => upload && !['PROCESSING', 'SENDING', 'SUCCESS', 'UNKNOWN'].includes(upload.status);
const pendingStatusExpired = upload => !!upload?.statusExpired && ['PROCESSING', 'SENDING', 'UNKNOWN'].includes(upload.status);
const ready = row => row.preview && row.validation?.valid &&
  row.validation.payloadDigest === row.preview.payloadDigest && !row.uploadUsage?.limitWarning &&
  (!['PROCESSING', 'SENDING', 'UNKNOWN'].includes(row.upload?.status) || pendingStatusExpired(row.upload)) && !row.stale;
const message = value => typeof value === 'object' ? JSON.stringify(value) : String(value);
const operationId = () => {
  const bytes = window.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

const panelSx = {
  bgcolor: '#fff', color: '#0f172a', borderRadius: '16px',
  border: '1px solid rgba(15, 23, 42, 0.06)',
  boxShadow: '0 4px 14px rgba(15, 23, 42, 0.06)',
  minWidth: 0, overflow: 'hidden',
};
const labelSx = { fontSize: '0.8125rem', fontWeight: 800, color: '#64748b' };
const primaryButtonSx = { bgcolor: '#0f172a', '&:hover': { bgcolor: '#1e293b' } };
const tones = {
  neutral: { bg: '#f1f5f9', color: '#475569' },
  success: { bg: '#ecfdf5', color: '#166534' },
  warning: { bg: '#fffbeb', color: '#92400e' },
  error: { bg: '#fef2f2', color: '#991b1b' },
  info: { bg: '#eff6ff', color: '#1e40af' },
};
const providerOptions = Object.entries(marketplaceProviders);
const catalogDate = value => value ? value.slice(0, 10).split('-').reverse().join('.') : '—';
const catalogProducts = preview =>
  (preview?.payload?.super_categories || []).flatMap(superCategory =>
    (superCategory.categories || []).flatMap(category => (category.sections || []).flatMap(section =>
      (section.products || []).map(product => ({
        ...product,
        categoryName: category.name,
      }))
    ))
  );
const needsReview = row => !!(row.error || row.stale || row.validation?.valid === false ||
  row.upload?.status === 'UNKNOWN' || pendingStatusExpired(row.upload) || terminalFailure(row.upload));
const uploadTone = upload => pendingStatusExpired(upload) ? 'warning'
  : upload?.status === 'SUCCESS' ? 'success'
  : ['SENDING', 'PROCESSING'].includes(upload?.status) ? 'info'
    : upload?.status === 'UNKNOWN' ? 'warning' : 'error';

function StatusChip({ label, tone = 'neutral' }) {
  return <Chip size="small" label={label} sx={{
    height: 'auto', minHeight: 28, maxWidth: '100%', fontSize: '0.8125rem', fontWeight: 800,
    bgcolor: tones[tone].bg, color: tones[tone].color,
    '& .MuiChip-label': { px: 0.9, py: 0.45, whiteSpace: 'normal', overflowWrap: 'anywhere', lineHeight: 1.25 },
  }} />;
}

function CatalogIssue({ issue }) {
  const entities = [
    ['Affected item', issue.entity], ['Parent', issue.parent], ['Target product', issue.target],
  ].filter(([, entity]) => entity);
  return <Alert severity="error" aria-label="Catalog issue" sx={{
    '& .MuiAlert-message': { width: '100%', minWidth: 0 },
  }}>
    <Typography sx={{ fontSize: '0.95rem', fontWeight: 900 }}>{issue.title}</Typography>
    <Typography sx={{ fontSize: '0.875rem', mt: 0.25 }}>{issue.description}</Typography>
    {!!entities.length && <Box component="dl" sx={{ m: 0, mt: 1, display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 0.7 }}>
      {entities.map(([label, entity]) => <Box key={label} sx={{ minWidth: 0 }}>
        <Typography component="dt" sx={{ ...labelSx, fontSize: '0.75rem' }}>{label}</Typography>
        <Typography component="dd" sx={{ m: 0, fontSize: '0.875rem', fontWeight: 800, overflowWrap: 'anywhere' }}>
          {entity.name || entity.id || 'Unknown'} <Box component="span" sx={{ color: '#64748b', fontWeight: 600 }}>· {entity.type}</Box>
        </Typography>
      </Box>)}
    </Box>}
    {!!issue.checks.length && <Box sx={{ mt: 1 }}>
      <Typography sx={{ ...labelSx, fontSize: '0.75rem' }}>Why it failed</Typography>
      {issue.checks.map(check => <Box key={check.code} sx={{ mt: 0.45 }}>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 800 }}>{check.title}</Typography>
        <Typography sx={{ fontSize: '0.8125rem', color: '#475569' }}>{check.description}</Typography>
      </Box>)}
    </Box>}
    <Box sx={{ mt: 1, p: 0.9, borderRadius: '8px', bgcolor: '#fff' }}>
      <Typography sx={{ ...labelSx, fontSize: '0.75rem' }}>How to fix</Typography>
      <Box component="ul" sx={{ m: 0, mt: 0.25, pl: 2.2 }}>
        {issue.actions.map(action => <Typography component="li" key={action} sx={{ fontSize: '0.8125rem', mt: 0.2 }}>{action}</Typography>)}
      </Box>
    </Box>
    <Box component="details" sx={{ mt: 0.75, color: '#64748b', fontSize: '0.75rem',
      '& summary': { cursor: 'pointer', fontWeight: 800 } }}>
      <summary>Technical details</summary>
      <Typography sx={{ fontSize: '0.75rem', mt: 0.4, overflowWrap: 'anywhere' }}>Error code: {issue.code}</Typography>
      {issue.stage && <Typography sx={{ fontSize: '0.75rem', overflowWrap: 'anywhere' }}>Stage: {issue.stage}</Typography>}
      {entities.filter(([, entity]) => entity.id).map(([label, entity]) => <Typography key={label} sx={{ fontSize: '0.75rem', overflowWrap: 'anywhere' }}>{label} ID: {entity.id}</Typography>)}
    </Box>
  </Alert>;
}

function Metric({ label, value, tone = 'neutral' }) {
  return <Box role="group" aria-label={label} sx={{
    bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px',
    px: 1.2, py: 0.8, minWidth: 0,
  }}>
    <Typography sx={{ ...labelSx, fontSize: '0.8125rem' }}>{label}</Typography>
    <Typography sx={{ color: tones[tone].color, fontSize: '1.25rem', fontWeight: 900, lineHeight: 1.3 }}>{value}</Typography>
  </Box>;
}

function CatalogWarnings({ warnings }) {
  if (!warnings.length) return <Stack direction="row" justifyContent="space-between" alignItems="center">
    <Typography sx={labelSx}>Catalog warnings</Typography>
    <StatusChip label="No warnings" />
  </Stack>;
  return <Box component="details" aria-label="Catalog warning details" sx={{
    '&[open] .catalog-warnings-chevron': { transform: 'rotate(180deg)' },
  }}>
    <Stack component="summary" direction="row" justifyContent="space-between" alignItems="center"
      aria-label={`Catalog warnings (${warnings.length})`} sx={{ cursor: 'pointer', listStyle: 'none',
        '&::-webkit-details-marker': { display: 'none' } }}>
      <Stack direction="row" spacing={0.35} alignItems="center">
        <ExpandMoreIcon className="catalog-warnings-chevron" sx={{ fontSize: 18, color: '#64748b', transition: 'transform 150ms ease' }} />
        <Typography sx={labelSx}>Catalog warnings</Typography>
      </Stack>
      <StatusChip label={`${warnings.length} to review`} tone="warning" />
    </Stack>
    <Alert severity="warning" sx={{ mt: 0.7, py: 0, '& .MuiAlert-message': { minWidth: 0, fontSize: '0.875rem', overflowWrap: 'anywhere' } }}>
      {warnings.map((warning, i) => <div key={i}>{message(warning)}</div>)}
    </Alert>
  </Box>;
}

function GlovoDescriptionPreview({ preview }) {
  const products = catalogProducts(preview);
  if (!products.length) return null;
  return <Box component="details" aria-label="Glovo product description details" sx={{
    '&[open] .glovo-descriptions-chevron': { transform: 'rotate(180deg)' },
  }}>
    <Stack component="summary" direction="row" justifyContent="space-between" alignItems="center"
      aria-label={`Glovo product descriptions (${products.length})`} sx={{ cursor: 'pointer', listStyle: 'none',
        '&::-webkit-details-marker': { display: 'none' } }}>
      <Stack direction="row" spacing={0.35} alignItems="center">
        <ExpandMoreIcon className="glovo-descriptions-chevron" sx={{ fontSize: 18, color: '#64748b', transition: 'transform 150ms ease' }} />
        <Typography sx={labelSx}>Glovo product descriptions</Typography>
      </Stack>
      <StatusChip label={`${products.length} items`} />
    </Stack>
    <Stack spacing={0.6} sx={{ mt: 0.7 }}>
      {products.map(product => <Box component="details" key={product.id} sx={{
          border: '1px solid #e2e8f0', borderRadius: '10px',
          bgcolor: '#f8fafc', overflow: 'hidden',
        }}>
        <Stack component="summary" direction="row" justifyContent="space-between" alignItems="center" spacing={1}
          aria-label={`Show Glovo description for ${product.name}`} sx={{ cursor: 'pointer', px: 1, py: 0.75 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 800, overflowWrap: 'anywhere' }}>{product.name}</Typography>
            <Typography sx={{ fontSize: '0.75rem', color: '#64748b', overflowWrap: 'anywhere' }}>{product.categoryName}</Typography>
          </Box>
          <StatusChip label={product.available ? 'Available' : 'Unavailable'} tone={product.available ? 'success' : 'warning'} />
        </Stack>
        <Box sx={{ px: 1, py: 0.9, borderTop: '1px solid #e2e8f0', bgcolor: '#fff' }}>
          <Typography sx={{ ...labelSx, fontSize: '0.75rem' }}>Description sent to Glovo</Typography>
          <Typography component="div" aria-label={`Glovo description for ${product.name}`} sx={{
            mt: 0.45, fontSize: '0.8125rem', lineHeight: 1.5, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere',
          }}>{product.description || 'Empty description'}</Typography>
          <Typography sx={{ mt: 0.65, fontSize: '0.75rem', color: '#64748b', overflowWrap: 'anywhere' }}>
            ID: {product.id} · Price: {product.price}
          </Typography>
        </Box>
        </Box>)}
    </Stack>
  </Box>;
}

function CatalogSummary({ row, provider, confirmation = false }) {
  const p = row.preview;
  const warnings = [...(p?.catalogWarnings || []), ...(row.validation?.warnings || [])];
  const list = p?.resolved.companyListDetails;
  return <Stack spacing={1.4}>
    {confirmation && <Typography component="h3" sx={{ fontWeight: 900, fontSize: '1.05rem' }}>{row.name}</Typography>}
    <Box component="dl" sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.1, m: 0,
      '& dt': labelSx, '& dd': { m: 0, fontSize: '0.875rem', fontWeight: 700, overflowWrap: 'anywhere' } }}>
      <Box><Typography component="dt">Provider</Typography><Typography component="dd">{provider.label}</Typography></Box>
      <Box><Typography component="dt">Environment</Typography><Typography component="dd">{p?.resolved.environment || row.environment}</Typography></Box>
      <Box sx={{ gridColumn: '1 / -1' }}><Typography component="dt">External store ID</Typography><Typography component="dd">{p?.resolved.storeId || row.storeId}</Typography></Box>
    </Box>
    <Box sx={{ p: 1.25, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px' }}>
      <Typography sx={labelSx}>Effective pricelist</Typography>
      <Typography sx={{ fontSize: '0.95rem', fontWeight: 900, mt: 0.2 }}>{p ? `${p.resolved.channelCompany.name} · v${list.version}` : 'Generate a preview'}</Typography>
      <Typography sx={{ fontSize: '0.8125rem', color: '#64748b', mt: 0.25 }}>{p ? `Effective from ${catalogDate(list.dateFrom)}` : 'The current pricelist and counts will appear here.'}</Typography>
    </Box>
    {p && <Box role="group" aria-label="Catalog counts" sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' }, gap: 0.6 }}>
      <Metric label="Products" value={p.counts.standaloneProducts ?? '—'} />
      <Metric label="Combos" value={p.counts.combos ?? '—'} />
      <Metric label="Unavailable" value={p.counts.unavailable ?? '—'} tone={p.counts.unavailable ? 'warning' : 'neutral'} />
      <Metric label="Total items" value={p.counts.products ?? '—'} />
    </Box>}
    {p && <CatalogWarnings warnings={warnings} />}
    {p && !confirmation && <GlovoDescriptionPreview preview={p} />}
    {p && <Box component="details" sx={{ fontSize: '0.8125rem', color: '#64748b',
      '& summary': { cursor: 'pointer', fontWeight: 800, py: 0.3 } }}>
      <summary>Preview details</summary>
      <Typography sx={{ fontSize: '0.8125rem', mt: 0.7 }}>Pricelist: {pricelistLabel(p)}</Typography>
      <Typography sx={{ fontSize: '0.8125rem', mt: 0.5, overflowWrap: 'anywhere', fontFamily: 'monospace' }}>Preview digest: {p.payloadDigest}</Typography>
    </Box>}
  </Stack>;
}

export default function MarketplaceOperations({ onReady, client: suppliedClient }) {
  const [client] = useState(() => suppliedClient || new Client());
  const [permissions] = useState(() => new Visibility('platforms_operations').scope);
  const [providerKey, setProviderKey] = useState('GLOVO_POS');
  const provider = marketplaceProviders[providerKey];
  const [rows, setRows] = useState([]);
  const [responseEnvironment, setResponseEnvironment] = useState('');
  const [selected, setSelected] = useState('');
  const [checked, setChecked] = useState([]);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [uploadEnabled, setUploadEnabled] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [closureConfirmation, setClosureConfirmation] = useState(null);
  const [closureDuration, setClosureDuration] = useState(30);
  const [requested, setRequested] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const detailPanel = useRef(null);
  const lock = useRef(false);
  const mounted = useRef(false);
  const expiryRefreshes = useRef(new Set());
  const allowed = permissions.visible;
  const canEdit = allowed && permissions.edit;
  const row = rows.find(r => r.location === selected);
  const environment = responseEnvironment || rows[0]?.environment;
  const environmentName = environment === 'prod' ? 'Production' : environment === 'staging' ? 'Staging' : environment;
  const readyCount = rows.filter(ready).length;
  const reviewCount = rows.filter(needsReview).length;
  const limitWarningCount = rows.filter(r => r.uploadUsage?.limitWarning).length;
  const previewCount = rows.filter(r => r.preview && !r.stale).length;
  const validatedCount = rows.filter(r => r.preview && r.validation?.valid && !r.stale &&
    r.validation.payloadDigest === r.preview.payloadDigest).length;
  const processingCount = rows.filter(r => ['SENDING', 'PROCESSING'].includes(r.upload?.status) && !pendingStatusExpired(r.upload)).length;
  const unknownCount = rows.filter(r => r.upload?.status === 'UNKNOWN' || pendingStatusExpired(r.upload)).length;
  const request = (action, data) => Promise.resolve(provider.request(client, action, data));
  const update = (id, changes) => {
    if (mounted.current) setRows(old => old.map(r => r.location === id ? { ...r, ...changes } : r));
  };

  async function run(label, fn) {
    if (lock.current) return;
    lock.current = true;
    setBusy(label); setError('');
    try { await fn(); }
    catch (e) { if (mounted.current) setError(marketplaceError(e)); }
    finally {
      lock.current = false;
      if (mounted.current) setBusy('');
    }
  }

  async function load(preserveResults = false) {
    const data = await request('menuLocations');
    if (!mounted.current) return;
    setUploadEnabled(data.uploadEnabled);
    setResponseEnvironment(data.environment || '');
    setRows(old => data.locations.map(next => {
      const previous = old.find(r => r.location === next.location);
      const sameMapping = previous?.storeId === next.storeId && previous?.environment === next.environment;
      const authoritativeUpload = next.lastUpload ? { status: next.lastUpload.outcome, ...next.lastUpload } : null;
      const retainedUpload = preserveResults && sameMapping ? previous.upload : null;
      const sameAttempt = retainedUpload && authoritativeUpload && (
        (retainedUpload.operationId && retainedUpload.operationId === authoritativeUpload.operationId) ||
        (retainedUpload.transactionId && retainedUpload.transactionId === authoritativeUpload.transactionId));
      return { ...(sameMapping ? previous : {}), ...next,
        upload: sameAttempt ? { ...retainedUpload, ...authoritativeUpload }
          : retainedUpload || authoritativeUpload || previous?.upload || null };
    }));
    setSelected(old => data.locations.some(r => r.location === old) ? old : '');
    setChecked(old => old.filter(id => data.locations.some(r => r.location === id)));
  }

  useEffect(() => {
    mounted.current = true;
    expiryRefreshes.current.clear();
    setRows([]); setResponseEnvironment(''); setSelected(''); setChecked([]); setRequested([]);
    setConfirmation(null); setClosureConfirmation(null); setClosureDuration(30);
    setUploadEnabled(false); setSearch(''); setStatusFilter('all');
    if (allowed) run('Loading locations', load);
    onReady?.();
    return () => { mounted.current = false; };
    // A provider change starts a fresh workflow; operation state is never shared.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerKey]);

  useEffect(() => {
    if (busy || !allowed) return undefined;
    const expiries = rows.filter(r => r.upload && !r.upload.statusExpired &&
      ['SENDING', 'PROCESSING', 'UNKNOWN'].includes(r.upload.status))
      .map(r => ({ key: `${r.storeId}:${r.environment}:${r.upload.statusExpiresOn}`,
        time: Date.parse(r.upload.statusExpiresOn) }))
      .filter(r => Number.isFinite(r.time) && !expiryRefreshes.current.has(r.key));
    if (!expiries.length) return undefined;
    const delay = Math.max(1000, Math.min(Math.min(...expiries.map(r => r.time)) - Date.now() + 1000, 2147483647));
    const timer = window.setTimeout(() => {
      expiries.filter(r => r.time <= Date.now()).forEach(r => expiryRefreshes.current.add(r.key));
      run('Refreshing locations', load);
    }, delay);
    return () => window.clearTimeout(timer);
    // The backend expiry timestamp schedules one authoritative location refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, busy, allowed]);

  useEffect(() => {
    if (!busy) return undefined;
    const guard = event => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', guard);
    return () => window.removeEventListener('beforeunload', guard);
  }, [busy]);

  async function preview(target) {
    try {
      const data = await request('menuPreview', { location: target.location });
      update(target.location, { preview: data, validation: null, stale: false, error: '', issue: null, uploadUsage: data.uploadUsage });
    } catch (e) { update(target.location, { error: marketplaceError(e), issue: marketplaceIssue(e), stale: true, validation: null }); }
  }

  async function validate(target) {
    try {
      const data = await request('menuValidate', { location: target.location, expectedDigest: target.preview.payloadDigest });
      update(target.location, { validation: data, error: '', issue: null, stale: false });
    } catch (e) {
      update(target.location, { validation: e.responseJSON?.valid === false ? e.responseJSON : null,
        error: marketplaceError(e), issue: marketplaceIssue(e), stale: e.responseJSON?.errorCode === 'MENU_CHANGED' || target.stale });
    }
  }

  async function prepare(targets = checked.length ? rows.filter(r => checked.includes(r.location)) : rows) {
    for (let offset = 0; offset < targets.length && mounted.current; offset += 2) {
      const group = targets.slice(offset, offset + 2);
      const data = await request('menuBatch', { action: 'prepare', items: group.map(r => ({ location: r.location })) });
      data.results.forEach(result => update(result.location, {
        ...(result.preview ? { preview: result.preview, uploadUsage: result.preview.uploadUsage } : {}),
        validation: result.validation || null,
        error: result.httpStatus >= 400 ? marketplaceError(result) : '',
        issue: result.httpStatus >= 400 ? marketplaceIssue(result) : null, stale: result.httpStatus >= 400,
      }));
    }
  }

  async function uploadConfirmed() {
    if (!confirmation || lock.current) return;
    const targets = confirmation;
    setConfirmation(null);
    await run('Uploading selected locations', async () => {
      setRequested(targets.map(r => r.location));
      targets.forEach(r => update(r.location, { upload: null }));
      for (let offset = 0; offset < targets.length && mounted.current; offset += 2) {
        const group = targets.slice(offset, offset + 2);
        group.forEach(r => update(r.location, { upload: { status: 'SENDING' }, error: '', issue: null }));
        try {
          const items = group.map(r => ({ location: r.location, expectedDigest: r.preview.payloadDigest, operationId: r.operationId }));
          const data = targets.length === 1 ? { results: [await request('menuUpload', items[0])] }
            : await request('menuBatch', { action: 'upload', items });
          data.results.forEach((result, i) => {
            const id = result.location || group[i].location;
            const failed = result.httpStatus >= 400 || (result.errorCode && !result.transactionId);
            update(id, {
              upload: { ...result, operationId: group[i].operationId, status: result.synchronizationState === 'UNKNOWN' ? 'UNKNOWN' : failed ? 'FAILED' : result.status || 'PROCESSING' },
              ...(result.uploadUsage ? { uploadUsage: result.uploadUsage } : {}),
              error: failed ? marketplaceError(result) : '', issue: failed ? marketplaceIssue(result) : null,
              stale: result.errorCode === 'MENU_CHANGED',
            });
          });
        } catch (e) {
          const knownRejection = ['FAILED', 'NOT_SENT'].includes(e.responseJSON?.synchronizationState) ||
            (e.status >= 400 && e.status < 500 && e.responseJSON?.errorCode && e.responseJSON?.synchronizationState !== 'UNKNOWN');
          group.forEach(r => update(r.location, {
            upload: { status: knownRejection ? 'FAILED' : 'UNKNOWN', operationId: r.operationId },
            error: marketplaceError(e), issue: marketplaceIssue(e), stale: e.responseJSON?.errorCode === 'MENU_CHANGED',
          }));
          // An unknown transport outcome stops the remaining queue; never retry.
          if (!knownRejection) break;
        }
      }
      try { await load(true); } catch (e) { if (mounted.current) setError(marketplaceError(e)); }
    });
  }

  async function refreshStatus(target) {
    try {
      const data = await request('menuUploadStatus', { transactionId: target.upload.transactionId });
      update(target.location, { upload: { ...target.upload, ...data }, error: '', issue: null });
    } catch (e) { update(target.location, { error: marketplaceError(e), issue: marketplaceIssue(e) }); }
  }

  async function refreshClosure(target) {
    update(target.location, { closure: { closureState: 'CHECKING', until: null, timezone: 'Europe/Bucharest' }, closureError: '' });
    try {
      const data = await request('temporaryClosureStatus', { location: target.location });
      update(target.location, { closure: data, closureError: '' });
    } catch (e) {
      update(target.location, {
        closure: { closureState: 'UNKNOWN', until: null, timezone: 'Europe/Bucharest' },
        closureError: temporaryClosureError(e),
      });
    }
  }

  async function applyClosureChange() {
    if (!closureConfirmation || lock.current) return;
    const change = closureConfirmation;
    setClosureConfirmation(null);
    await run(change.action === 'SET' ? 'Closing Glovo store' : 'Restoring Glovo schedule', async () => {
      const target = rows.find(item => item.location === change.location);
      if (!target) return;
      try {
        if (change.action === 'SET') {
          await request('temporaryClosureSet', {
            location: target.location,
            until: futureClosureTimestamp(change.duration),
          });
        } else {
          await request('temporaryClosureRemove', { location: target.location });
        }
        await refreshClosure(target);
      } catch (e) {
        const unknown = e?.responseJSON?.operationState === 'UNKNOWN' || e?.status === 0;
        update(target.location, {
          ...(unknown ? { closure: { closureState: 'UNKNOWN', until: null, timezone: 'Europe/Bucharest' } } : {}),
          closureError: temporaryClosureError(e),
        });
      }
    });
  }

  function confirm(targets) {
    if (!canEdit) return;
    setConfirmation(targets.filter(ready).map(target => ({ ...target, operationId: operationId() })));
  }

  function reviewLocation(id) {
    setSelected(id);
    const target = rows.find(item => item.location === id);
    if (target) refreshClosure(target);
    if (window.innerWidth < 1200) detailPanel.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }

  if (!allowed) return <Alert severity="error">You do not have permission to manage marketplace menus.</Alert>;
  const results = rows.filter(r => requested.includes(r.location));
  const eligible = rows.filter(r => checked.includes(r.location) && ready(r));
  const visibleRows = rows.filter(r => {
    const matchesSearch = `${r.name} ${r.storeId}`.toLowerCase().includes(search.trim().toLowerCase());
    const matchesStatus = statusFilter === 'all' || (statusFilter === 'ready' && ready(r)) ||
      (statusFilter === 'review' && needsReview(r)) || (statusFilter === 'limit' && r.uploadUsage?.limitWarning) ||
      (statusFilter === 'processing' && ['SENDING', 'PROCESSING'].includes(r.upload?.status) && !pendingStatusExpired(r.upload));
    return matchesSearch && matchesStatus;
  });
  const visibleChecked = visibleRows.filter(r => checked.includes(r.location)).length;
  const hiddenChecked = checked.length - visibleChecked;
  const filtersActive = !!search.trim() || statusFilter !== 'all';
  const checkLabel = checked.length ? `Check selected (${checked.length})` : `Check all (${rows.length})`;
  const selectVisible = value => setChecked(old => value
    ? [...new Set([...old, ...visibleRows.map(r => r.location)])]
    : old.filter(id => !visibleRows.some(r => r.location === id)));
  const selectReady = () => setChecked(old => [...new Set([...old, ...visibleRows.filter(ready).map(r => r.location)])]);
  const nextStep = row?.issue ? 'Resolve the catalog issue below, then generate the preview again.'
    : !row?.preview ? 'Start with Preview to review this location’s catalog.'
    : row.stale ? 'Run Preview again to review the latest catalog.'
      : pendingStatusExpired(row.upload) ? 'The previous result is unknown. Review the menu, then confirm a new upload if needed.'
      : row.upload?.status === 'UNKNOWN' ? 'Reconcile the unknown upload before publishing again.'
        : ['SENDING', 'PROCESSING'].includes(row.upload?.status) ? 'Check the provider status before another upload.'
          : row.uploadUsage?.limitWarning ? 'The local daily upload limit has been reached.'
            : !row.validation?.valid ? 'Validate this preview with Glovo before uploading.'
              : 'Ready to upload. You will review a confirmation first.';

  return <Box component="main" aria-label="Platform Operations" sx={{
    width: '100%', boxSizing: 'border-box', textAlign: 'left',
    minHeight: { lg: 'calc(100dvh - 64px)' }, height: { lg: 'calc(100dvh - 64px)' },
    overflow: { lg: 'hidden' },
    p: { xs: 0.75, sm: 1.25, lg: 1.5 }, display: 'flex', flexDirection: 'column', gap: 1.25,
    color: '#0f172a',
    '& .MuiButton-root': { textTransform: 'none', fontWeight: 800, borderRadius: '10px', fontSize: '0.875rem', minHeight: { xs: 40, sm: 34 } },
    '& .MuiAlert-root': { borderRadius: '12px', fontSize: '0.875rem' },
  }}>
    <Box component="header" sx={{ ...panelSx, p: { xs: 1.25, sm: 1.5 }, flexShrink: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.25, mb: 1.2 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography component="h1" sx={{ fontWeight: 900, fontSize: { xs: '1.1rem', sm: '1.35rem' }, color: '#020617', lineHeight: 1.2 }}>Platform Operations</Typography>
          <Typography sx={{ color: '#64748b', fontSize: '0.875rem', mt: 0.35 }}>Review catalogs, validate and publish by location.</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.8 }}>
          <Box role="group" aria-label="Active provider">
            <FormControl size="small" sx={{ minWidth: 136 }}>
              <InputLabel id="marketplace-provider-label">Provider</InputLabel>
              <Select labelId="marketplace-provider-label" label="Provider" value={providerKey} disabled={!!busy}
                sx={{ height: 40, borderRadius: '10px', bgcolor: '#f8fbff', fontSize: '0.95rem', fontWeight: 800,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#bfdbfe' } }}
                onChange={e => setProviderKey(e.target.value)}>
                {providerOptions.map(([key, p]) => <MenuItem key={key} value={key}>{p.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
          <Box role="group" aria-label="Environment" sx={{ minWidth: 94, height: 40,
            boxSizing: 'border-box', display: 'flex', flexDirection: 'column', justifyContent: 'center',
            px: 1.1, borderRadius: '10px',
            border: `1px solid ${environment === 'prod' ? '#fecaca' : '#cbd5e1'}`,
            bgcolor: environment === 'prod' ? '#fef2f2' : '#f8fafc' }}>
            <Typography sx={{ ...labelSx, fontSize: '0.6875rem', lineHeight: 1.2 }}>Environment</Typography>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 900, lineHeight: 1.2,
              color: environment === 'prod' ? '#b91c1c' : '#1d4ed8' }}>{environmentName || '—'}</Typography>
          </Box>
          <Tooltip title="Refresh locations and recover upload transactions">
            <span><Button size="small" aria-label="Refresh locations" startIcon={<RefreshIcon />} disabled={!!busy}
              sx={{ height: 40, px: 1, whiteSpace: 'nowrap' }} onClick={() => run('Loading locations', load)}>Refresh</Button></span>
          </Tooltip>
        </Box>
      </Box>
      <Box role="group" aria-label="Overview" sx={{ display: 'grid', width: '100%', maxWidth: 800,
        gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(4, minmax(0, 1fr))' },
        gap: { xs: 0.6, sm: 1 } }}>
        <Metric label="Mapped locations" value={rows.length} />
        <Metric label="Ready" value={readyCount} tone={readyCount ? 'success' : 'neutral'} />
        <Metric label="Needs review" value={reviewCount} tone={reviewCount ? 'error' : 'neutral'} />
        <Metric label="Upload-limit warnings" value={limitWarningCount} tone={limitWarningCount ? 'warning' : 'neutral'} />
      </Box>
      {busy && <Box role="status" aria-live="polite" sx={{ mt: 1 }}>
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}><CircularProgress size={12} /><Typography sx={{ fontSize: '0.8125rem', color: '#64748b' }}>{busy}</Typography></Stack>
        <LinearProgress sx={{ height: 2, borderRadius: 2 }} />
      </Box>}
    </Box>
    {error && <Alert severity="error" sx={{ flexShrink: 0 }}>{error}</Alert>}
    {!busy && !uploadEnabled && <Alert severity="warning" sx={{ flexShrink: 0, py: 0 }}>Glovo mutations are disabled by backend configuration. Preview and closure-status reads remain available.</Alert>}

    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1.6fr) minmax(340px, 1fr)' },
      gap: 1.25, alignItems: 'stretch', flex: 1, minHeight: { lg: 0 }, overflow: { lg: 'hidden' } }}>
      <Paper component="section" aria-label="Locations" elevation={0} sx={{ ...panelSx,
        display: 'flex', flexDirection: 'column', minHeight: 0, height: { lg: '100%' } }}>
        <Box sx={{ p: 1.25, borderBottom: '1px solid #e2e8f0', flexShrink: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
              <Typography component="h2" sx={{ fontSize: '1rem', fontWeight: 900 }}>Locations</Typography>
              <Typography sx={{ fontSize: '0.8125rem', color: '#64748b' }}>{visibleRows.length} of {rows.length}</Typography>
            </Box>
            <Stack direction="row" spacing={0.5}>
              {filtersActive && <Button size="small" onClick={() => { setSearch(''); setStatusFilter('all'); }}>Clear filters</Button>}
              <Button size="small" disabled={!!busy || !visibleRows.some(ready)} onClick={selectReady}>Select ready</Button>
            </Stack>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
            <TextField label="Search locations" size="small" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Location or store ID" sx={{ flex: { xs: '1 1 100%', sm: '1 1 240px' }, maxWidth: { sm: 360 },
                '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '0.875rem' } }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                endAdornment: search && <InputAdornment position="end"><IconButton size="small" aria-label="Clear search" onClick={() => setSearch('')}><CloseIcon fontSize="small" /></IconButton></InputAdornment> }} />
            <FormControl size="small" sx={{ minWidth: 160, flex: { xs: '1 1 100%', sm: '0 0 160px' } }}>
              <InputLabel id="marketplace-status-label">Location status</InputLabel>
              <Select labelId="marketplace-status-label" label="Location status" value={statusFilter}
                sx={{ borderRadius: '10px', fontSize: '0.875rem' }} onChange={e => setStatusFilter(e.target.value)}>
                <MenuItem value="all">All locations</MenuItem><MenuItem value="ready">Ready to upload</MenuItem>
                <MenuItem value="review">Needs review</MenuItem><MenuItem value="limit">Upload limit</MenuItem><MenuItem value="processing">Processing</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
        <Box sx={{ px: 1.25, py: 1, borderBottom: '1px solid #e2e8f0', bgcolor: '#f8fafc', flexShrink: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75 }}>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 800, fontVariantNumeric: 'tabular-nums', mr: { md: 'auto' } }}>{checked.length} selected · {eligible.length} ready to upload</Typography>
            {/* Reserve the longest labels so changing selection cannot move the actions. */}
            <Button size="small" variant="outlined" startIcon={<CheckCircleOutlineIcon />} disabled={!!busy || !rows.length || !uploadEnabled || !canEdit} onClick={() => run('Checking locations', prepare)}>
              <Box component="span" sx={{ display: 'grid', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                <Box component="span" aria-hidden="true" sx={{ gridArea: '1 / 1', visibility: 'hidden' }}>Check selected ({rows.length})</Box>
                <Box component="span" sx={{ gridArea: '1 / 1' }}>{checkLabel}</Box>
              </Box>
            </Button>
            <Button size="small" variant="contained" disableElevation startIcon={<CloudUploadIcon />} sx={primaryButtonSx} disabled={!!busy || !uploadEnabled || !canEdit || !eligible.length} onClick={() => confirm(eligible)}>
              <Box component="span" sx={{ display: 'grid', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                <Box component="span" aria-hidden="true" sx={{ gridArea: '1 / 1', visibility: 'hidden' }}>Upload ready ({rows.length})</Box>
                <Box component="span" sx={{ gridArea: '1 / 1' }}>Upload ready ({eligible.length})</Box>
              </Box>
            </Button>
            <Tooltip title="Check previews and validates selected locations, or all when none are selected. Upload sends only selected locations that are ready.">
              <IconButton size="small" aria-label="How selection actions work" sx={{ color: '#64748b' }}><InfoOutlinedIcon fontSize="small" /></IconButton>
            </Tooltip>
            <Tooltip title="Clear selection">
              <span><Button size="small" aria-label="Clear selection" disabled={!!busy || !checked.length} onClick={() => setChecked([])}>Clear</Button></span>
            </Tooltip>
          </Box>
          {filtersActive && !!hiddenChecked && <Typography sx={{ fontSize: '0.8125rem', color: '#92400e', mt: 0.5 }} role="status">
            {hiddenChecked} selected {hiddenChecked === 1 ? 'location is' : 'locations are'} hidden by your filters.
          </Typography>}
        </Box>
        <TableContainer role="region" aria-label="Locations table" tabIndex={0} sx={{
          overflow: 'auto', flex: 1, minHeight: 0, overscrollBehavior: 'contain' }}>
          <Table stickyHeader size="small" aria-label="Platform locations" sx={{ minWidth: 490,
            '& .MuiTableCell-root': { px: 1, py: 0.65, borderColor: '#f1f5f9', fontSize: '0.875rem' },
            '& .MuiTableCell-head': { py: 0.8, bgcolor: '#f8fafc', color: '#64748b', fontSize: '0.8125rem', fontWeight: 800 } }}>
            <TableHead><TableRow>
              <TableCell padding="checkbox"><Checkbox size="small" inputProps={{ 'aria-label': 'Select visible locations' }}
                disabled={!!busy || !visibleRows.length} checked={!!visibleRows.length && visibleChecked === visibleRows.length}
                indeterminate={visibleChecked > 0 && visibleChecked < visibleRows.length} onChange={e => selectVisible(e.target.checked)} /></TableCell>
              <TableCell>Location</TableCell><TableCell>Pricelist</TableCell><TableCell>Status</TableCell>
              <TableCell>Uploads today</TableCell>
            </TableRow></TableHead>
            <TableBody>{visibleRows.map(r => <TableRow key={r.location} hover selected={selected === r.location}
              sx={{ '&.Mui-selected': { bgcolor: '#eff6ff' }, '&.Mui-selected:hover': { bgcolor: '#e0edff' } }}>
              <TableCell padding="checkbox"><Checkbox size="small" inputProps={{ 'aria-label': `Select ${r.name}` }} checked={checked.includes(r.location)} disabled={!!busy}
                onChange={e => setChecked(old => e.target.checked ? [...old, r.location] : old.filter(id => id !== r.location))} /></TableCell>
              <TableCell sx={{ minWidth: 120 }}><Button size="small" aria-label={`Review ${r.name}`} aria-pressed={selected === r.location} disabled={!!busy} onClick={() => reviewLocation(r.location)}
                sx={{ p: 0, minWidth: 0, justifyContent: 'flex-start', textAlign: 'left', fontSize: '0.875rem', lineHeight: 1.3 }}>{r.name}</Button>
                <Typography sx={{ fontSize: '0.8125rem', color: '#64748b', lineHeight: 1.3, overflowWrap: 'anywhere' }}>{r.storeId}</Typography></TableCell>
              <TableCell><Tooltip title={r.preview ? pricelistLabel(r.preview) : 'Pricelist appears after a preview'}><Box>
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: r.preview ? '#0f172a' : '#94a3b8' }}>{r.preview ? `${r.preview.resolved.channelCompany.name} · v${r.preview.resolved.companyListDetails.version}` : '—'}</Typography>
              </Box></Tooltip></TableCell>
              <TableCell sx={{ minWidth: 118 }}><Stack spacing={0.45} alignItems="flex-start">
                {r.stale ? <StatusChip label="Preview required" tone="warning" />
                  : r.validation ? <StatusChip label={r.validation.valid ? 'Validation OK' : 'Validation failed'} tone={r.validation.valid ? 'success' : 'error'} />
                    : <StatusChip label={r.preview ? 'Preview OK' : 'Not checked'} tone={r.preview ? 'success' : 'neutral'} />}
                {r.upload && <Tooltip title={uploadLabel(r.upload)}><Box><StatusChip label={r.upload.status === 'UNKNOWN' ? 'Upload unknown' : uploadLabel(r.upload)} tone={uploadTone(r.upload)} /></Box></Tooltip>}
                {r.error && <Tooltip title={r.error}><Typography sx={{ fontSize: '0.8125rem', color: '#b91c1c', maxWidth: 180,
                  display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2, overflow: 'hidden', overflowWrap: 'anywhere' }}>{r.error}</Typography></Tooltip>}
              </Stack></TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}><Typography sx={{ fontSize: '0.875rem', fontWeight: 800, color: r.uploadUsage?.limitWarning ? '#b45309' : '#334155' }}>{r.uploadUsage?.count ?? '—'} / {r.uploadUsage?.limit ?? 5}</Typography>
                {r.uploadUsage?.limitWarning && <Typography sx={{ fontSize: '0.8125rem', color: '#b45309' }}>Limit warning</Typography>}</TableCell>
            </TableRow>)}</TableBody>
          </Table>
          {!visibleRows.length && !busy && <Box sx={{ p: 3, textAlign: 'center' }}><StorefrontIcon sx={{ color: '#94a3b8', fontSize: 30, mb: 0.75 }} />
            <Typography sx={{ color: '#64748b', fontSize: '0.875rem' }}>{rows.length ? 'No locations match your filters.' : 'No eligible Glovo location mappings were found.'}</Typography>
          </Box>}
        </TableContainer>
      </Paper>

      <Paper component="section" aria-label={row ? 'Selected location' : 'All locations'} ref={detailPanel} elevation={0} sx={{ ...panelSx, display: 'flex', flexDirection: 'column',
        minHeight: 0, height: { lg: '100%' }, scrollMarginTop: '76px' }}>
        <Box sx={{ px: 1.5, py: 1.25, borderBottom: '1px solid #e2e8f0', flexShrink: 0 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Typography sx={labelSx}>{row ? 'Selected location' : 'Operations overview'}</Typography>
            {row?.upload && <StatusChip label={row.upload.status === 'UNKNOWN' ? 'Upload unknown' : uploadLabel(row.upload)} tone={uploadTone(row.upload)} />}
          </Stack>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
            <Typography component="h2" sx={{ fontWeight: 900, fontSize: '1.1rem', mt: 0.25 }}>{row?.name || 'All locations'}</Typography>
            {row && <Button size="small" disabled={!!busy} onClick={() => setSelected('')}>All locations</Button>}
          </Box>
        </Box>
        {row ? <>
          <Box sx={{ p: 1.5, borderBottom: '1px solid #e2e8f0', bgcolor: '#f8fafc', flexShrink: 0 }}>
            <Typography sx={{ ...labelSx, mb: 0.75 }}>Preview → Validate → Upload</Typography>
            <Stack direction="row" spacing={0.6} sx={{ flexWrap: 'wrap', rowGap: 0.75 }}>
              <Button size="small" variant="outlined" startIcon={<VisibilityIcon />} sx={{ flex: 1 }} disabled={!!busy} onClick={() => run('Generating preview', () => preview(row))}>Preview</Button>
              <Button size="small" variant="outlined" startIcon={<CheckCircleOutlineIcon />} sx={{ flex: 1 }} disabled={!!busy || !row.preview || row.stale || !uploadEnabled || !canEdit} onClick={() => run('Validating with Glovo', () => validate(row))}>Validate</Button>
              <Button size="small" variant="contained" disableElevation startIcon={<CloudUploadIcon />} sx={{ ...primaryButtonSx, flex: 1 }} disabled={!!busy || !uploadEnabled || !canEdit || !ready(row)} onClick={() => confirm([row])}>Upload</Button>
            </Stack>
            <Typography sx={{ fontSize: '0.875rem', color: '#64748b', mt: 0.9 }}>{nextStep}</Typography>
          </Box>
          <Box role="region" aria-label="Location operation details" tabIndex={0} sx={{
            p: 1.5, minHeight: 0, flex: { lg: 1 }, overflowY: { lg: 'auto' }, overscrollBehavior: 'contain' }}><Stack spacing={1.4}>
            {row.upload && <Box sx={{ p: 1.2, borderRadius: '12px', bgcolor: tones[uploadTone(row.upload)].bg }}>
              <Typography sx={{ ...labelSx, mb: 0.5 }}>Latest upload</Typography>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 800, color: tones[uploadTone(row.upload)].color }}>{uploadLabel(row.upload)}</Typography>
              {row.upload.errorCode && <Typography sx={{ fontSize: '0.8125rem', mt: 0.5 }}>{row.upload.errorCode}</Typography>}
              {row.upload.validationErrors?.map((detail, i) => <Typography sx={{ fontSize: '0.8125rem' }} key={i}>{message(detail)}</Typography>)}
              {row.upload.transactionId && <Typography sx={{ overflowWrap: 'anywhere', fontSize: '0.8125rem', mt: 0.5 }}>Transaction ID: {row.upload.transactionId}</Typography>}
              {row.upload.details?.map((detail, i) => <Typography sx={{ fontSize: '0.8125rem', mt: 0.5 }} key={i}>{message(detail)}</Typography>)}
              {pendingStatusExpired(row.upload) && <Typography sx={{ fontSize: '0.8125rem', mt: 0.5 }}>
                Glovo may have applied the previous upload. A new full upload can replace the menu; verify the final menu and item availability afterward.
              </Typography>}
              <Button size="small" startIcon={<RefreshIcon />} sx={{ mt: 0.7 }} disabled={!!busy || !row.upload.transactionId || pendingStatusExpired(row.upload)} onClick={() => run('Refreshing upload status', () => refreshStatus(row))}>Refresh status</Button>
            </Box>}
            <Box role="group" aria-label="Temporary store closure" sx={{ p: 1.2, borderRadius: '12px', border: '1px solid #e2e8f0', bgcolor: '#fff' }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 0.7 }}>
                <Typography sx={labelSx}>Temporary closure</Typography>
                <StatusChip
                  label={row.closure?.closureState === 'TEMPORARILY_CLOSED' ? 'Temporarily closed'
                    : row.closure?.closureState === 'NORMAL_SCHEDULE' ? 'Normal schedule'
                      : row.closure?.closureState === 'UNKNOWN' ? 'Status unknown'
                        : row.closure?.closureState === 'CHECKING' ? 'Checking' : 'Not checked'}
                  tone={row.closure?.closureState === 'TEMPORARILY_CLOSED' ? 'warning'
                    : row.closure?.closureState === 'NORMAL_SCHEDULE' ? 'success'
                      : row.closure?.closureState === 'UNKNOWN' ? 'error' : 'neutral'}
                />
              </Stack>
              {row.closure?.closureState === 'TEMPORARILY_CLOSED' && <Typography sx={{ fontSize: '0.875rem', fontWeight: 800 }}>
                Closed until {bucharestTime(row.closure.until)} (Europe/Bucharest)
              </Typography>}
              {row.closure?.closureState === 'NORMAL_SCHEDULE' && <Typography sx={{ fontSize: '0.8125rem', color: '#64748b' }}>
                No temporary closure. Glovo follows its regular schedule; this does not prove the store is currently open.
              </Typography>}
              {row.closure?.closureState === 'UNKNOWN' && <Typography sx={{ fontSize: '0.8125rem', color: '#991b1b' }}>
                Provider status is unknown. Refresh status before making another change.
              </Typography>}
              {!row.closure && <Typography sx={{ fontSize: '0.8125rem', color: '#64748b' }}>
                Refresh provider status before changing the schedule.
              </Typography>}
              {row.closureError && <Alert severity="error" sx={{ mt: 0.8, py: 0 }}>{row.closureError}</Alert>}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.7} sx={{ mt: 1 }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel id="temporary-closure-duration-label">Duration</InputLabel>
                  <Select labelId="temporary-closure-duration-label" label="Duration" value={closureDuration}
                    disabled={!!busy || !canEdit || !uploadEnabled || !['NORMAL_SCHEDULE', 'TEMPORARILY_CLOSED'].includes(row.closure?.closureState)}
                    onChange={e => setClosureDuration(Number(e.target.value))}>
                    <MenuItem value={15}>15 minutes</MenuItem><MenuItem value={30}>30 minutes</MenuItem>
                    <MenuItem value={60}>1 hour</MenuItem><MenuItem value={120}>2 hours</MenuItem>
                  </Select>
                </FormControl>
                <Button variant="contained" disableElevation sx={primaryButtonSx}
                  disabled={!!busy || !canEdit || !uploadEnabled || !['NORMAL_SCHEDULE', 'TEMPORARILY_CLOSED'].includes(row.closure?.closureState)}
                  onClick={() => setClosureConfirmation({ action: 'SET', location: row.location, name: row.name, duration: closureDuration })}>
                  Close temporarily
                </Button>
                <Button variant="outlined" disabled={!!busy || !canEdit || !uploadEnabled || row.closure?.closureState !== 'TEMPORARILY_CLOSED'}
                  onClick={() => setClosureConfirmation({ action: 'REMOVE', location: row.location, name: row.name })}>
                  Return to normal schedule
                </Button>
              </Stack>
              <Button size="small" startIcon={<RefreshIcon />} sx={{ mt: 0.7 }} disabled={!!busy}
                onClick={() => run('Refreshing temporary closure', () => refreshClosure(row))}>Refresh closure status</Button>
            </Box>
            <CatalogSummary row={row} provider={provider} />
            {row.issue ? <CatalogIssue issue={row.issue} /> : row.error && <Alert severity="error">{row.error}</Alert>}
            {row.validation && <Alert severity={row.validation.valid ? 'success' : 'error'} sx={{ py: 0 }}>{row.validation.valid ? 'Validation OK' : 'Validation failed'}{row.validation.errors?.map((e, i) => <div key={i}>{message(e)}</div>)}</Alert>}
            <Box component="details" sx={{ color: '#64748b', fontSize: '0.8125rem', '& summary': { cursor: 'pointer', fontWeight: 700 } }}>
              <summary><InfoOutlinedIcon sx={{ fontSize: 14, verticalAlign: 'text-bottom', mr: 0.5 }} />About the daily upload limit</summary>
              <Typography sx={{ fontSize: '0.8125rem', mt: 0.75 }}>Glovo’s approximate limit is 5 complete uploads per store/day. Counts use Europe/Bucharest days and attempts recorded by this release, including unknown outcomes. Uploads outside Kalopsia are not included.</Typography>
            </Box>
          </Stack></Box>
        </> : <Stack role="region" aria-label="Operations summary" tabIndex={0} spacing={1.5} sx={{
          p: 1.5, minHeight: 0, flex: { lg: 1 }, overflowY: { lg: 'auto' }, overscrollBehavior: 'contain' }}>
          <Typography sx={{ fontSize: '0.875rem', color: '#475569' }}>Choose a location in the table to review its catalog.</Typography>
          <Box component="dl" role="group" aria-label="Catalog progress" sx={{ m: 0, display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1,
            '& dt': { ...labelSx, fontSize: '0.8125rem' },
            '& dd': { m: 0, fontSize: '1.25rem', fontWeight: 900, lineHeight: 1.35 },
            '& dd span': { fontSize: '0.8125rem', fontWeight: 700, color: '#64748b' } }}>
            <Box role="group" aria-label="Previews available"><Typography component="dt">Previews available</Typography><Typography component="dd">{previewCount} <Box component="span">/ {rows.length}</Box></Typography></Box>
            <Box role="group" aria-label="Validated catalogs"><Typography component="dt">Validated catalogs</Typography><Typography component="dd">{validatedCount} <Box component="span">/ {rows.length}</Box></Typography></Box>
          </Box>
          {(processingCount > 0 || unknownCount > 0) && <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
            {processingCount > 0 && <StatusChip label={`Processing uploads: ${processingCount}`} tone="info" />}
            {unknownCount > 0 && <StatusChip label={`Unknown upload outcomes: ${unknownCount}`} tone="warning" />}
          </Box>}
          <Box>
            <Button size="small" variant="outlined" startIcon={<CheckCircleOutlineIcon />} disabled={!!busy || !rows.length || !uploadEnabled || !canEdit}
              onClick={() => run('Checking all locations', () => prepare(rows))}>Check all locations ({rows.length})</Button>
            <Typography sx={{ fontSize: '0.8125rem', color: '#64748b', mt: 0.7 }}>Previews and validates every mapped location; it does not upload.</Typography>
          </Box>
          <Box component="details" sx={{ fontSize: '0.8125rem', color: '#64748b',
            '& summary': { cursor: 'pointer', fontWeight: 800, py: 0.3 } }}>
            <summary>About eligible locations</summary>
            <Typography sx={{ fontSize: '0.8125rem', mt: 0.6 }}>Only active locations with valid {provider.label} store mappings are listed. Check all includes every mapped location, even when a table filter or selection is active. Uploads require a separate confirmation.</Typography>
          </Box>
        </Stack>}
      </Paper>
    </Box>

    {requested.length > 1 && <Paper component="section" aria-label="Batch result" elevation={0} sx={{ ...panelSx, flexShrink: 0, display: 'flex', flexDirection: 'column', maxHeight: 210 }}>
      <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid #e2e8f0' }}><Typography component="h2" sx={{ fontSize: '0.95rem', fontWeight: 900 }}>Batch result</Typography>
        <Typography sx={{ fontSize: '0.8125rem', color: '#64748b', mt: 0.25 }}>Requested: {requested.length} · Success: {results.filter(r => r.upload?.status === 'SUCCESS').length} · Processing: {results.filter(r => ['SENDING', 'PROCESSING'].includes(r.upload?.status) && !pendingStatusExpired(r.upload)).length} · Failed: {results.filter(r => terminalFailure(r.upload)).length} · Unknown: {results.filter(r => r.upload?.status === 'UNKNOWN' || pendingStatusExpired(r.upload)).length} · Not started: {requested.length - results.filter(r => r.upload).length}</Typography>
      </Box>
      <Box sx={{ overflowY: 'auto', px: 1.5, py: 0.5 }}>{results.map(r => <Box key={r.location} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, py: 0.5 }}>
        <Typography sx={{ fontSize: '0.875rem', color: r.upload ? tones[uploadTone(r.upload)].color : '#64748b' }}>{r.name}: {uploadLabel(r.upload) || 'Not started'}</Typography>
        <IconButton size="small" aria-label={`Refresh ${r.name} status`} disabled={!!busy || !r.upload?.transactionId || pendingStatusExpired(r.upload)} onClick={() => run('Refreshing upload status', () => refreshStatus(r))}><RefreshIcon fontSize="small" /></IconButton>
      </Box>)}</Box>
    </Paper>}

    <Dialog open={!!closureConfirmation} onClose={() => !busy && setClosureConfirmation(null)} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: '16px' } }}>
      <DialogTitle sx={{ fontSize: '1.2rem', fontWeight: 900 }}>
        {closureConfirmation?.action === 'SET' ? 'Temporarily close this Glovo store?' : 'Return this Glovo store to its normal schedule?'}
      </DialogTitle>
      <DialogContent dividers><Stack spacing={1}>
        <Typography sx={{ fontSize: '0.9rem', fontWeight: 800 }}>{closureConfirmation?.name}</Typography>
        <Typography sx={{ fontSize: '0.875rem', color: '#475569' }}>
          {closureConfirmation?.action === 'SET'
            ? `Glovo will override the regular schedule for ${closureConfirmation?.duration} minutes.`
            : 'The temporary closure will be removed. Glovo will then follow the regular store schedule.'}
        </Typography>
      </Stack></DialogContent>
      <DialogActions sx={{ p: 1.5 }}>
        <Button onClick={() => setClosureConfirmation(null)} disabled={!!busy}>Cancel</Button>
        <Button variant="contained" disableElevation sx={primaryButtonSx} onClick={applyClosureChange} disabled={!!busy}>
          {closureConfirmation?.action === 'SET' ? 'Confirm temporary closure' : 'Confirm normal schedule'}
        </Button>
      </DialogActions>
    </Dialog>

    <Dialog open={!!confirmation} onClose={() => !busy && setConfirmation(null)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: '16px' } }}>
      <DialogTitle sx={{ fontSize: '1.2rem', fontWeight: 900 }}>Publish this menu to {provider.label}?</DialogTitle>
      <DialogContent dividers><Stack spacing={2} divider={<Divider />}>
        <Alert severity="warning" sx={{ borderRadius: '12px', fontSize: '0.875rem' }}>Review each store, environment, pricelist and warning. Uploads run independently and remain processing until Glovo confirms SUCCESS.</Alert>
        {confirmation?.some(target => pendingStatusExpired(target.upload)) && <Alert severity="warning">
          One or more previous uploads have an unknown outcome after 24 hours. Glovo may have applied them. A new full upload can replace the menu; verify the final menu and item availability afterward.
        </Alert>}
        {confirmation?.map(target => <CatalogSummary key={target.location} row={target} provider={provider} confirmation />)}
      </Stack></DialogContent>
      <DialogActions sx={{ p: 1.5 }}><Button onClick={() => setConfirmation(null)} disabled={!!busy} sx={{ textTransform: 'none' }}>Cancel</Button><Button variant="contained" disableElevation startIcon={<CloudUploadIcon />} onClick={uploadConfirmed} disabled={!!busy} sx={{ ...primaryButtonSx, textTransform: 'none', borderRadius: '10px' }}>Confirm upload</Button></DialogActions>
    </Dialog>
  </Box>;
}
